import { Button } from "@/components/ui/button.tsx";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card.tsx";
import { m } from "@/paraglide/messages.js";
import { Link } from "@tanstack/react-router";
import { FileQuestion, Home, MessageSquare, Undo2 } from "lucide-react";
import { lazy, Suspense, useState } from "react";

// Lazy because these two components are values passed to createRouter as
// `defaultErrorComponent` / `defaultNotFoundComponent`, so the router plugin
// cannot code-split them — they sit in the ENTRY chunk, paid on /auth/login. The
// support dialog is behind a useState toggle and pulls ui/dialog.tsx, which
// nothing else in the entry closure needs.
const SupportDialog = lazy(() =>
	import("@/components/support-dialog").then((module) => ({
		default: module.SupportDialog,
	})),
);

export function NotFound({ children }: { children?: React.ReactNode }) {
	const [supportOpen, setSupportOpen] = useState(false);

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="w-full max-w-2xl shadow-lg">
				<CardHeader className="text-center space-y-3 pb-6">
					<div className="flex justify-center">
						<div className="rounded-full bg-muted p-4">
							<FileQuestion className="h-12 w-12 text-muted-foreground" />
						</div>
					</div>
					<CardTitle className="text-3xl font-semibold">
						{m.notfound_title()}
					</CardTitle>
					<CardDescription className="text-base">
						{m.notfound_subtitle()}
					</CardDescription>
				</CardHeader>

				<CardContent className="space-y-6">
					{children ? (
						<div className="bg-muted/50 rounded-lg p-4">
							<p className="text-sm text-muted-foreground">{children}</p>
						</div>
					) : (
						<div className="bg-muted/50 rounded-lg p-4 space-y-4">
							<p className="text-sm text-muted-foreground">
								{m.notfound_description()}
							</p>
							<div className="space-y-2">
								<p className="text-sm font-medium text-foreground">
									{m.notfound_suggestions()}
								</p>
								<ul className="space-y-2 text-sm text-muted-foreground">
									<li className="flex items-start gap-2">
										<span className="text-primary mt-0.5">{"•"}</span>
										<span>{m.notfound_check_url()}</span>
									</li>
									<li className="flex items-start gap-2">
										<span className="text-primary mt-0.5">{"•"}</span>
										<span>{m.notfound_use_navigation()}</span>
									</li>
									<li className="flex items-start gap-2">
										<span className="text-primary mt-0.5">{"•"}</span>
										<span>{m.notfound_search_content()}</span>
									</li>
									<li className="flex items-start gap-2">
										<span className="text-primary mt-0.5">{"•"}</span>
										<span>{m.notfound_contact_support()}</span>
									</li>
								</ul>
							</div>
						</div>
					)}
				</CardContent>

				<CardFooter className="flex flex-col gap-3 pt-6">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
						<Button
							render={<Link to="/dashboard" />}
							variant="default"
							className="w-full gap-2"
						>
							<Home className="h-4 w-4" />
							{m.notfound_actions_go_home()}
						</Button>

						<Button
							variant="outline"
							className="w-full gap-2"
							onClick={() => {
								window.history.back();
							}}
						>
							<Undo2 className="h-4 w-4" />
							{m.notfound_actions_go_back()}
						</Button>
					</div>

					<Button
						onClick={() => setSupportOpen(true)}
						variant="secondary"
						className="w-full gap-2"
					>
						<MessageSquare className="h-4 w-4" />
						{m.notfound_actions_contact_support()}
					</Button>
				</CardFooter>
			</Card>

			{supportOpen && (
				<Suspense fallback={null}>
					<SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
				</Suspense>
			)}
		</div>
	);
}
